// src/data/tableCards.js
// Take It to the Table: one conversation question per module, engineered
// for a table of people who have never read a page of philosophy. The app
// produces thinking with nowhere to go — these give it a door out.
//
// Authoring rules (hold these on every edit):
//   • One question, sayable from memory after one read. The setup is two
//     or three sentences a reader can retell over dinner without notes.
//   • No philosopher's name required to ask it. Names live in `guidance`,
//     for the reader who wants to bring the module along afterwards.
//   • Every card is drawn from the module's own content — the same story
//     the reader just met, turned outward.
//   • The question must split a normal table. A question everyone answers
//     the same way is a speech, not a conversation.
//
// `sides` maps the classic positions in plain speech (name in brackets,
// jargon second) so a table can find where each person sits after the
// question lands — never before it.
//
// Field names (`description`, `question`, `guidance`) are deliberate:
// they're on the Knowledge Hub extractor's field list, so Sofia can serve
// these when a reader asks how to talk about a module with other people.
//
// Australian English throughout.

export const TABLE_CARDS = [
  {
    moduleOrder: 1,
    moduleId: 'what-is-philosophy',
    handle: 'The Opinion Rule',
    description:
      'People say it to end arguments politely: "everyone\'s entitled to their opinion." It sounds like good manners, and nobody ever pushes on it.',
    question:
      'Is every opinion actually worth the same, or are some opinions better than others? And who gets to say which?',
    sides: [
      "Every opinion deserves a hearing: that's just respect (the democratic instinct)",
      "Some opinions are earned: reasons and evidence rank them (Socrates' side)",
      "Depends what it's about: taste is yours, facts aren't (the dissolving move)",
    ],
    guidance:
      'Most tables split between "of course some are better" and "who are you to judge?", and the second group usually judges opinions all the time (they just did). Follow up with: "name one opinion you hold that you\'d genuinely want someone to talk you out of." That\'s the Socratic move from Module 1: the whole method in one dinner question.',
  },
  {
    moduleOrder: 2,
    moduleId: 'logic-and-reasoning',
    handle: 'Winning or Finding Out',
    description:
      'Think of the last proper argument you had with someone you love. Not a blow-up, just a real disagreement that went a few rounds.',
    question:
      'Were you trying to win, or trying to find out what was true? And which would the other person say you were doing?',
    sides: [
      'Arguing is for winning: persuasion is how the world runs (the Sophists)',
      'Arguing is for finding out: a lost argument that corrects you is a gift (Socrates)',
    ],
    guidance:
      'The second half is the engine: people rate themselves truth-seekers and each other point-scorers, and the table notices the gap immediately. If it lands, go one deeper: "what would it cost you to lose an argument well?" That\'s the fork Module 2 opens with: the Sophists argued to win, Socrates treated being refuted as a favour.',
  },
  {
    moduleOrder: 3,
    moduleId: 'epistemology',
    handle: 'The Stopped Clock',
    description:
      'You glance at the station clock. It says two o\'clock, and it really is two o\'clock, so you walk off knowing the time. What you never find out: that clock stopped exactly twelve hours ago.',
    question:
      'Did you know the time, or did you just get lucky?',
    sides: [
      'You knew: you believed it, with good reason, and you were right (the classical definition of knowledge)',
      "You got lucky, and luck isn't knowledge (Russell's point, sharpened by Gettier)",
    ],
    guidance:
      'Everyone understands the puzzle in ten seconds and almost nobody agrees on the answer. If the table says "lucky", ask what would have to be added to make it knowledge; if it says "knew", point out the clock was broken: they\'d have "known" at any time of day. Bertrand Russell\'s example, and the heart of Module 3: being right isn\'t the same as knowing, which matters in a world where everyone is sure of something.',
  },
  {
    moduleOrder: 4,
    moduleId: 'ethics-moral-philosophy',
    handle: 'The Lever and the Footbridge',
    description:
      'A runaway trolley will kill five people. You can pull a lever to divert it onto a side track, where it will kill one person instead. Most people pull the lever. Now change one thing: no lever. The only way to stop the trolley is to push a large stranger off a footbridge into its path. Same trade, five for one.',
    question:
      'Would you pull the lever? Would you push the man? And if you answered differently, what changed? The arithmetic didn\'t.',
    sides: [
      'Count the outcomes: five lives beat one, both times (consequentialism; Mill)',
      'Some acts are wrong whatever they save: never use a person merely as a tool (Kant)',
      "Ask what a good person would do, not which rule wins (Aristotle's virtue ethics)",
    ],
    guidance:
      'Ask the two questions in order and count hands each time; the room usually flips between them, and the fun is making people defend the flip. Don\'t rescue the table with the answer; there isn\'t a settled one. Module 4\'s two big moral traditions part ways at exactly this footbridge: weighing outcomes versus refusing to use a person as a tool.',
  },
  {
    moduleOrder: 5,
    moduleId: 'metaphysics-reality',
    handle: 'The Teleporter',
    description:
      'A machine scans your body down to the last atom, destroys it, and instantly builds a perfect copy on Mars: same memories, same laugh, same half-finished plans. To everyone who knows you, the person who steps out is simply you, arrived.',
    question:
      'Do you step in? And if you hesitated, what exactly is the thing you\'re afraid won\'t make the trip?',
    sides: [
      "You're the pattern: memories and personality travel; that's still you (psychological continuity; Locke, Parfit)",
      "You're the matter: this body is you, and the arrival is a twin (the body theory)",
      "'Same person' was always a convention, not a fact (the Buddhist dissolve)",
    ],
    guidance:
      'The split is instant and visceral: "of course, that\'s still me" versus "that\'s a copy walking around with my life." Push the confident ones with: "would you step in if the original was destroyed a minute AFTER the copy woke up?" Module 5 runs this alongside the Ship of Theseus: whether a thing, or a person, is the stuff or the pattern.',
  },
  {
    moduleOrder: 6,
    moduleId: 'philosophy-of-mind',
    handle: 'The Perfect Impersonation',
    description:
      'AI chat already says "I understand how you feel" as smoothly as any friend. Imagine it gets so good that a machine could text you for a year (jokes, comfort at 2am, remembering your mother\'s surgery) and you\'d never pick it from a person.',
    question:
      'Would anything actually be understanding you? Or only ever be brilliant at seeming to? And how could you tell the difference?',
    sides: [
      'The whole system understands, even if no single part does (functionalism)',
      "Fluent symbol-shuffling isn't understanding, however perfect (Searle)",
      "If you could never tell the difference, the difference doesn't matter (the pragmatist shrug)",
    ],
    guidance:
      'Watch for the table\'s second wave: someone always asks "does the difference even matter if I can\'t detect it?" and someone else always finds that it matters enormously. That\'s the Chinese Room from Module 6 running at the table: a system passing every test in a language while, arguably, nothing inside understands a word. There\'s no consensus answer after more than forty years, so nobody at the table can lose.',
  },
  {
    moduleOrder: 7,
    moduleId: 'social-political-philosophy',
    handle: 'The Law You\'d Break',
    description:
      'Rosa Parks broke the law, and nearly everyone now says she was right to. So breaking the law can be the right thing to do, which quietly hands every one of us a live question, not just the heroes of history.',
    question:
      'Which law, today, would you be right to break? And how do you know you\'re a Rosa Parks and not just someone who doesn\'t like a rule?',
    sides: [
      "Fairness first: judge every law from behind the veil, not knowing who you'd be (Rawls)",
      "Liberty first: the law's main job is to leave people alone (Nozick's libertarianism)",
      'Break unjust laws openly, non-violently, and accept the penalty (King, after Gandhi)',
    ],
    guidance:
      'The first half opens people up; the second half keeps them honest. If the table stalls, offer King\'s test from his Birmingham jail letter (Module 7): break the law openly, non-violently, and accept the penalty. The willingness to pay marks the difference between conscience and convenience. Expect real disagreement about which current laws qualify; that disagreement is the conversation.',
  },
  {
    moduleOrder: 8,
    moduleId: 'existentialism-meaning',
    handle: 'The Demon\'s Offer',
    description:
      'A demon appears tonight and tells you: this life, exactly as you\'ve lived it, you will live again, and again, forever. Every joy, every humiliation, every Tuesday, in the same order. Nothing edited.',
    question:
      'Is your gut answer yes or no? And what would have to change about your life to change your answer?',
    sides: [
      "Say yes: build a life you could sign for eternally (Nietzsche's affirmation)",
      'Curse the demon, then get to work: meaning is made, not found (Camus)',
      'The longing itself points at something real to be found (Rumi and the finders)',
    ],
    guidance:
      'Let people answer the gut question first. The honest majority curse the demon, and saying so out loud is oddly bonding. The second question is where it turns from a party trick into a conversation, because it asks everyone to name the thing they\'d fix. Nietzsche built the test for exactly that information (Module 8); it pairs well with asking whether meaning is found or made.',
  },
];

export const getTableCard = (moduleOrder) =>
  TABLE_CARDS.find((c) => c.moduleOrder === moduleOrder) || null;
